/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { ReactNode } from "react";

// @mui material components
import type { Icon as TablerIcon } from "@tabler/icons-react";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";

// Timeline context
import { useTimeline } from "examples/Timeline/context";
import { Divider, Grid, Typography } from "@mui/material";

interface Props {
  color?: string;
  name?: string;
  icon: TablerIcon;
  title?: string;
  dateTime: string;
  description?: ReactNode;
  lastItem?: boolean;
  firstItem?: boolean;
  position?: "left" | "right";
  [key: string]: any;
}

function TimelineItem({
  color,
  name,
  icon: IconEvento,
  title,
  dateTime,
  description,
  lastItem,
  firstItem,
  position = "left",
}: Props) {
  const isDark = useTimeline();

  // Offset de la línea/punto: pegado a la izquierda en mobile, centrado en desktop
  const lineLeft = { xs: 0, md: "50%" };
  const lineTransform = { xs: "none", md: "translateX(-50%)" };

  const iconoCirculo = (
    <Grid item xs={3} sx={{ display: { xs: "none", md: "block" } }}>
      <MDBox
        position="relative"
        display="flex"
        justifyContent="center"
        alignItems="center"
        bgColor={color}
        width="4rem"
        height="4rem"
        borderRadius="50%"
        zIndex={3}
        flexShrink={0}
      >
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="2.5rem"
          height="2.5rem"
          borderRadius="50%"
          bgColor="#fff"
          sx={{ boxShadow: "-2px 2px 6px -1px rgba(0,0,0,0.85)" }}
          zIndex={4}
        >
          <IconEvento size={32} stroke={2} color={color} />
        </MDBox>
      </MDBox>
    </Grid>
  );

  return (
    <MDBox
      position="relative"
      minHeight="120px"
      display="flex"
      sx={{
        justifyContent: {
          xs: "flex-start",
          md: position === "left" ? "flex-start" : "flex-end",
        },
      }}
      mb={0}
    >
      {/* Línea vertical superior */}
      <MDBox
        position="absolute"
        top="0"
        height="100%"
        width="8px"
        bgColor={color}
        zIndex={1}
        sx={{ left: lineLeft, transform: lineTransform }}
      />

      {/* Punto/dot */}
      <MDBox
        position="absolute"
        borderRadius="50%"
        bgColor={color}
        zIndex={2}
        sx={{
          left: { xs: "-8px", md: "50%" },
          transform: lineTransform,
          bottom: {
            xs: "50%",
            md: 0,
          },
          width: {
            xs: "25px",
            md: "25px",
          },
          height: {
            xs: "25px",
            md: "25px",
          },
        }}
      >
        {/* Flecha: en mobile siempre apunta hacia el contenido (a la derecha) */}
        <MDBox
          position="absolute"
          width="0"
          height="0"
          zIndex={2}
          sx={{
            top: {
              xs: "50%",
              md: "50%",
            },
            left: {
              xs: "calc(50% + 8px)",
              md: position === "left" ? "auto" : "calc(50% + 15px)",
            },
            right: {
              xs: "auto",
              md: position === "left" ? "calc(50% + 15px)" : "auto",
            },
            transform: "translateY(-50%)",
            borderTop: "10px solid transparent",
            borderBottom: "10px solid transparent",
            borderRight: {
              xs: "none",
              md: position === "left" ? `10px solid ${color}` : "none",
            },
            borderLeft: {
              xs: `10px solid ${color}`,
              md: position === "right" ? `10px solid ${color}` : "none",
            },
          }}
        />
        <MDBox
          position="absolute"
          top="50%"
          left="50%"
          width="10px"
          height="10px"
          borderRadius="50%"
          bgColor="#fff"
          sx={{
            transform: "translate(-50%, -50%)",
            boxShadow: "-2px 2px 6px -1px rgba(0,0,0,0.85)",
          }}
          zIndex={3}
        />
      </MDBox>

      {/* Línea vertical inferior (conector al siguiente item) */}
      {!lastItem && (
        <MDBox
          position="absolute"
          top="calc(50% + 6px)"
          height="calc(50% - 6px)"
          width="4px"
          bgcolor="grey.300"
          zIndex={1}
          sx={{ left: lineLeft, transform: lineTransform }}
        />
      )}

      {/* Contenido */}
      <MDBox
        mt={7}
        sx={{
          width: { xs: "calc(100% - 55px)", md: "45%" },
          ml: { xs: "55px", md: 0 },
          textAlign: {
            xs: "left",
            md: position === "left" ? "right" : "left",
          },
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            textAlign: {
              xs: "left",
              md: position === "left" ? "left" : "right",
            },
          }}
        >
          {position === "left" && iconoCirculo}
          <Grid item xs={12} md={9}>
            <Typography variant="h6" gutterBottom sx={{ display: "block" }}>
              {title}
            </Typography>
            <Divider
              sx={{
                mt: 0,
                mb: 1,
                borderStyle: "dashed",
                borderColor: "grey.700",
                borderBottomWidth: 2,
              }}
            />
            <Typography variant="caption" gutterBottom sx={{ display: "block" }}>
              {description}
            </Typography>
            <Typography variant="caption" gutterBottom sx={{ display: "block" }}>
              {dateTime}
            </Typography>
          </Grid>
          {position === "right" && iconoCirculo}
        </Grid>
      </MDBox>
    </MDBox>
  );
}

TimelineItem.defaultProps = {
  color: "info",
  lastItem: false,
  description: "",
};

export default TimelineItem;
